const BOOKABLE_AVAILABILITY_VARIANTS = new Set([
  'available',
  'last-places',
])

function normalizeText(value) {
  return typeof value === 'string'
    ? value.trim().toLocaleLowerCase('pl-PL')
    : ''
}

function getAudience(program) {
  const groups = new Set(
    (program?.terms ?? [])
      .map(term => normalizeText(term.group))
      .filter(Boolean)
  )

  if (groups.size === 0) return 'unknown'

  const womenOnly = [...groups].every(group =>
    group.includes('tylko dla kobiet') || group === 'dla kobiet'
  )

  return womenOnly ? 'women-only' : 'mixed'
}

function audienceScore(source, candidate) {
  const sourceAudience = getAudience(source)
  const candidateAudience = getAudience(candidate)

  if (
    sourceAudience === 'mixed' &&
    candidateAudience === 'women-only'
  ) {
    return null
  }

  if (sourceAudience === candidateAudience) return 30
  if (sourceAudience === 'women-only' && candidateAudience === 'mixed') {
    return 24
  }

  return 0
}

function travelStyleScore(source, candidate) {
  const sourceStyles = new Set(source?.travelStyles ?? [])
  const sharedStyles = (candidate?.travelStyles ?? [])
    .filter(style => sourceStyles.has(style))

  if (sharedStyles.length === 0) return 0
  return Math.min(45, 40 + (sharedStyles.length - 1) * 5)
}

function durationScore(source, candidate) {
  if (
    source?.durationDays == null ||
    candidate?.durationDays == null
  ) {
    return 0
  }

  const sourceDuration = Number(source?.durationDays)
  const candidateDuration = Number(candidate?.durationDays)

  if (!Number.isFinite(sourceDuration) || !Number.isFinite(candidateDuration)) {
    return 0
  }

  const difference = Math.abs(sourceDuration - candidateDuration)
  if (difference <= 2) return 15
  if (difference <= 4) return 8
  if (difference <= 6) return 3
  return 0
}

function priceScore(source, candidate) {
  const sourcePrice = Number(source?.priceFrom)
  const candidatePrice = Number(candidate?.priceFrom)

  if (
    !Number.isFinite(sourcePrice) ||
    !Number.isFinite(candidatePrice) ||
    sourcePrice <= 0
  ) {
    return 0
  }

  const difference = Math.abs(sourcePrice - candidatePrice) / sourcePrice
  if (difference <= 0.15) return 10
  if (difference <= 0.3) return 6
  if (difference <= 0.5) return 3
  return 0
}

export function hasAvailablePlaces(term) {
  return BOOKABLE_AVAILABILITY_VARIANTS.has(term?.availabilityVariant)
}

export function toAvailableProgram(program) {
  const terms = (program?.terms ?? [])
    .filter(hasAvailablePlaces)
    .sort((first, second) =>
      String(first.startDate).localeCompare(String(second.startDate))
    )

  if (terms.length === 0) return null

  const prices = terms
    .map(term => Number(term.price))
    .filter(price => Number.isFinite(price) && price > 0)
  const uniquePrices = new Set(prices)

  return {
    ...program,
    terms,
    durationDays: terms[0]?.durationDays ?? program.durationDays,
    duration: terms[0]?.duration ?? program.duration,
    priceFrom: prices.length > 0 ? Math.min(...prices) : program.priceFrom,
    pricesDiffer: uniquePrices.size > 1,
  }
}

export function scoreProgramRecommendation(source, candidate) {
  const audience = audienceScore(source, candidate)
  if (audience === null) return null

  return audience +
    travelStyleScore(source, candidate) +
    (source?.continent && source.continent === candidate?.continent ? 20 : 0) +
    durationScore(source, candidate) +
    priceScore(source, candidate)
}

function compareRecommendations(first, second) {
  return second.score - first.score ||
    (first.program.recommendedOrder ?? Number.MAX_SAFE_INTEGER) -
      (second.program.recommendedOrder ?? Number.MAX_SAFE_INTEGER) ||
    String(first.program.programKey).localeCompare(
      String(second.program.programKey),
      'pl-PL'
    )
}

export function getRecommendedPrograms(
  programs,
  currentProgramKey,
  limit = 3,
  sourceOverride = null
) {
  const availablePrograms = programs
    .map(toAvailableProgram)
    .filter(Boolean)
  const source = availablePrograms.find(
    program => program.programKey === currentProgramKey
  ) ?? programs.find(
    program => program.programKey === currentProgramKey
  ) ?? sourceOverride

  return availablePrograms
    .filter(program => program.programKey !== currentProgramKey)
    .map(program => ({
      program,
      score: source
        ? scoreProgramRecommendation(source, program)
        : 0,
    }))
    .filter(item => item.score !== null)
    .sort(compareRecommendations)
    .slice(0, Math.max(0, Number(limit) || 0))
    .map(item => item.program)
}
